import { defineComponent as _defineComponent } from 'vue'
import { ref, Ref, computed, watch, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';

import store from '@/store';
import { useFilterStore } from '@/stores';
import { toastServiceError } from '@/services/core/notification';
import { SelectOptionsService } from '@/services';

import { FilterIcon } from '@heroicons/vue/solid';

import {
    CallActions,
    CallFilter,
    CallGetters,
    CallParticipantType,
    CallStatus,
    CallType,
    LevelModel,
    SelectOptionsServiceGetContactPhoneNumbersResult,
    SelectOptionsServiceGetMediasResult,
    SelectOptionsServiceGetUserLabelsResult,
    SelectOptionsServiceGetUsersResult,
    SelectOptionsServiceGetWordsResult,
} from '@/types';


export default /*@__PURE__*/_defineComponent({
  __name: 'CallListFilters',
  setup(__props, { expose: __expose }) {
  __expose();

const { t } = useI18n();
const route = useRoute();
const filterStore = useFilterStore();

const filtersOpened = ref(false);
const selectedParticipant = ref<CallParticipantType>(CallParticipantType.All);
const filter: Ref<CallFilter> = ref({
    type: null,
    status: null,
    userLabel: null,
    media: null,
    numberClient: null,
    user: null,
    word: null,
});

const wordsOptions: Ref<SelectOptionsServiceGetWordsResult[]> = ref([]);
const userLabelOptions: Ref<SelectOptionsServiceGetUserLabelsResult[]> = ref([]);
const agentOptions: Ref<SelectOptionsServiceGetUsersResult[]> = ref([]);
const mediaFilterOptions: Ref<SelectOptionsServiceGetMediasResult[]> = ref([]);
const clientNumberOptions: Ref<SelectOptionsServiceGetContactPhoneNumbersResult[]> = ref([]);

const levelFilter = computed<LevelModel | null>(() => filterStore.level);
const storeFilters = computed<CallFilter>(() => store.getters[CallGetters.FILTER]);

const isCallTypeRoute = computed(() => {
    return Object.values(CallType)
        .map((value) => value.toLowerCase())
        .some((callTypeValue) => route.path.toLowerCase().includes(callTypeValue));
});

const isCallStatusRoute = computed(() => {
    return Object.values(CallStatus)
        .map((value) => value.toLowerCase())
        .some((callStatusValue) => route.path.toLowerCase().includes(callStatusValue));
});

const isLoadingWords = ref(false);

const statusFilterOptions = [
    { label: t(`call.status.${CallStatus.ANSWERED}`), value: CallStatus.ANSWERED },
    { label: t(`call.status.${CallStatus.BUSY}`), value: CallStatus.BUSY },
    { label: t(`call.status.${CallStatus.NO_ANSWER}`), value: CallStatus.NO_ANSWER },
    { label: t(`call.status.${CallStatus.DITCHED}`), value: CallStatus.DITCHED },
];

const typeFilterOptions = [
    { label: t(`call.type.${CallType.INBOUND}`), value: CallType.INBOUND },
    { label: t(`call.type.${CallType.OUTBOUND}`), value: CallType.OUTBOUND },
    { label: t(`call.type.${CallType.INTERNAL}`), value: CallType.INTERNAL },
];

const callParticipantOptions = [
    { label: t('word.participant.ALL'), value: CallParticipantType.All },
    { label: t('word.participant.AGENT'), value: CallParticipantType.Agent },
    { label: t('word.participant.CLIENT'), value: CallParticipantType.Client },
];

const refreshLocalFilter = () => {
    filter.value.type = storeFilters.value.type;
    filter.value.status = storeFilters.value.status;
    filter.value.userLabel = storeFilters.value.userLabel;
    filter.value.media = storeFilters.value.media;
    filter.value.numberClient = storeFilters.value.numberClient;
    filter.value.user = storeFilters.value.user;
    filter.value.word = storeFilters.value.word;
};

const applyFilters = () => {
    store.dispatch(CallActions.SET_FILTER, filter.value);
};

const clearFilters = () => {
    store.dispatch(CallActions.CLEAR_FILTER);
    refreshLocalFilter();
};

const fetchWords = async () => {
    try {
        isLoadingWords.value = true;
        const response = await SelectOptionsService.getWords({
            params: {
                call_participant: selectedParticipant.value,
            },
        });
        wordsOptions.value = response.data;
    } catch (error) {
        toastServiceError(error);
        wordsOptions.value = [];
    } finally {
        isLoadingWords.value = false;
    }
};

const fetchUserLabels = async () => {
    try {
        const response = await SelectOptionsService.getUserLabels();
        userLabelOptions.value = response.data;
    } catch (error) {
        toastServiceError(error);
    }
};

const fetchAgents = async () => {
    try {
        const response = await SelectOptionsService.getUsers();
        agentOptions.value = response.data;
    } catch (error) {
        toastServiceError(error);
    }
};

const fetchMedias = async () => {
    try {
        const response = await SelectOptionsService.getMedias();
        mediaFilterOptions.value = response.data;
    } catch (error) {
        toastServiceError(error);
        mediaFilterOptions.value = [];
    }
};

const fetchClientNumbers = async () => {
    const { data } = await SelectOptionsService.getContactPhoneNumbers();
    clientNumberOptions.value = data;
};

const filterCallParticipantTypes = (value: CallParticipantType) => {
    selectedParticipant.value = value || CallParticipantType.All;
    filter.value.word = null;
};

const getButtonVariant = (participantType: CallParticipantType) => {
    if (selectedParticipant.value != participantType) {
        return 'gray-100';
    }

    switch (participantType) {
        case CallParticipantType.Agent:
            return 'purple-200';
        case CallParticipantType.Client:
            return 'blue-200';
        default:
            return 'gray-200';
    }
};

const getTextVariant = (participantType: CallParticipantType) => {
    switch (participantType) {
        case CallParticipantType.Agent:
            return 'purple-700';
        case CallParticipantType.Client:
            return 'blue-700';
        default:
            return 'gray-700';
    }
};

onMounted(() => {
    fetchUserLabels();
    fetchAgents();
    fetchWords();
    fetchClientNumbers();
    fetchMedias();
});

watch(filtersOpened, () => {
    refreshLocalFilter();
});

watch(levelFilter, () => {
    store.dispatch(CallActions.CLEAR_FILTER);
    fetchUserLabels();
    fetchAgents();
    fetchWords();
    fetchClientNumbers();
    fetchMedias();
});

watch(selectedParticipant, () => {
    fetchWords();
});

const __returned__ = { t, route, filterStore, filtersOpened, selectedParticipant, filter, wordsOptions, userLabelOptions, agentOptions, mediaFilterOptions, clientNumberOptions, levelFilter, storeFilters, isCallTypeRoute, isCallStatusRoute, isLoadingWords, statusFilterOptions, typeFilterOptions, callParticipantOptions, refreshLocalFilter, applyFilters, clearFilters, fetchWords, fetchUserLabels, fetchAgents, fetchMedias, fetchClientNumbers, filterCallParticipantTypes, getButtonVariant, getTextVariant, get FilterIcon() { return FilterIcon } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})